import { DocumentDownloadOptions, DownloadFormat, Fonts, TextAlignment } from '../../../../../backend/src/types/types';
import { FilterField } from '@/api/queries/useGetMetadataFilterOptions';

export type DownloadKind = 'written' | 'table' | 'list' | 'zip' | 'image' | 'bundle';
export type ImageFormat = 'png' | 'svg';
export type AnyFormat = DownloadFormat | ImageFormat | 'zip' | 'pdf';

export interface ColumnsSpec {
  available: string[];
  default: string[];
  relativity?: FilterField[];
}

export interface DownloadFormValues<TFormat extends AnyFormat = AnyFormat> {
  filename: string;
  format: TFormat;
  scope?: string;
  columns?: string[];
  relativityColumns?: string[];
  style?: string;
  docOptions?: DocumentDownloadOptions;
  includeSourceDocuments?: boolean;
  orderByDate?: boolean;
}

interface BaseVariant<TFormat extends AnyFormat = AnyFormat> {
  id: string;
  kind: DownloadKind;
  label: string;
  description?: string;
  icon?: string;
  formats: TFormat[];
  defaultFilename?: string;
  columns?: ColumnsSpec;
  style?: boolean;
  docOptions?: boolean;
  sourceDocs?: boolean;
  largeExportWarning?: boolean;
}

/**
 * Post-creation contract shared by any variant that runs as a tracked async export
 * (standard async variants and bundles).
 */
export interface ExportProgress<TDownloadArg> {
  /** SSE endpoint base URL (exportId appended as ?exportId= by the progress modal). */
  progressEndpoint(exportId: string): string;
  /**
   * Optional REST endpoint URL builder (exportId appended as `?exportId=`)
   * returning the persisted terminal status. The progress modal polls it when
   * the SSE channel drops. Sibling to `progressEndpoint` and gated by the same
   * per-resource download permission.
   */
  statusEndpoint?(exportId: string): string;
  /** Called when the user clicks "Download" on the ready progress modal. */
  downloadReady(exportId: string, arg: TDownloadArg | undefined): Promise<void>;
  readyDescription?: string;
  downloadButtonText?: string;
}

export type AsyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> &
  ExportProgress<TFormat> & {
    mode: 'async';
    mutate(values: DownloadFormValues<TFormat>): Promise<{ exportId: string; jobId?: string }>;
  };

export type SyncVariant<TFormat extends AnyFormat = AnyFormat> = BaseVariant<TFormat> & {
  mode: 'sync';
  mutate(values: DownloadFormValues<TFormat>): Promise<void>;
};

export interface BundleVariantConfig {
  matterId: string;
  subsetId: string;
  itemType: 'doc' | 'fact' | 'inconsistency' | 'entity';
  getItems: () => Array<{
    id: string;
    schemaName: 'doc' | 'fact' | 'inconsistency' | 'entity';
    schemaId: string;
    originalName: string;
    pageCount?: number;
    dateText?: string | null;
  }>;
  defaultName: string;
}

export interface BundleVariant extends ExportProgress<string> {
  id: string;
  kind: 'bundle';
  label: string;
  description?: string;
  icon?: string;
  bundle: BundleVariantConfig;
}

export type DownloadVariant<TFormat extends AnyFormat = AnyFormat> =
  | AsyncVariant<TFormat>
  | SyncVariant<TFormat>
  | BundleVariant;

export const isBundleVariant = (v: DownloadVariant): v is BundleVariant => v.kind === 'bundle';

export interface DownloadScope {
  id: string;
  label: string;
  totalCount?: number;
}

export interface DownloadDefinition {
  title?: string;
  scopes?: DownloadScope[];
  defaultScopeId?: string;
  variants: DownloadVariant[];
  defaultVariantId?: string;
  totalCount?: number;
}

export const defaultDocOptions = (): DocumentDownloadOptions => ({
  fontFamily: Fonts.TimesNewRoman,
  textAlignment: TextAlignment.Left,
  includeReferences: true,
});

export const kindShowsFilename = (kind: DownloadKind): boolean => kind !== 'image' && kind !== 'bundle';

export const defaultIconForKind = (kind: DownloadKind): string => {
  switch (kind) {
    case 'table':
      return 'lucide--table';
    case 'written':
      return 'lucide--file-text';
    case 'list':
      return 'lucide--list';
    case 'zip':
      return 'lucide--folder-archive';
    case 'image':
      return 'lucide--image';
    case 'bundle':
      return 'lucide--package';
    default: {
      const _exhaustive: never = kind;
      return _exhaustive;
    }
  }
};
